/**
 * Release 3 "Notify, deliver, see" components (docs/specs/release3-contract.md): notifications, board follow and
 * activity (lane A), share-link policy, find similar and board defaults (lane B), export presets (lane C), private
 * boards and board guests (lane D). Names match `components.schemas`. Enums used by core schemas (NotificationKind,
 * NotificationChannel, ShareVersionMode, BoardVisibility, ExportFormat, GuestAccess) live in common.ts; field
 * additions in assets.ts (`share_excluded`), shareLinks.ts (policy), boards.ts (`visibility`, `owner_member_id`,
 * `followed`), members.ts (`is_guest`), versions.ts (`JobKind.export_render`).
 *
 * Each lane appends only inside its own `// --- release3/X ---` section.
 */
import { z } from 'zod';
import {
  ActorRef,
  ApprovalStatus,
  Email,
  ExportFormat,
  GuestAccess,
  Id,
  JsonObject,
  NotificationChannel,
  NotificationKind,
  Timestamp,
  UnitInterval,
  atLeastOneField,
  cursorPage,
} from './common.js';
import { AssetCard } from './assets.js';
import { Comment } from './comments.js';
import { Member } from './members.js';

// --- release3/A --- notifications (REV-1), board follow + activity (REV-2)

export const NotificationDigest = z.enum(['daily', 'twice_daily']);
export type NotificationDigest = z.infer<typeof NotificationDigest>;

export const NotificationSubjectType = z.enum(['asset', 'board', 'comment', 'share_link', 'workspace']);
export type NotificationSubjectType = z.infer<typeof NotificationSubjectType>;

export const Notification = z.object({
  id: Id,
  workspace_id: Id,
  /** The recipient; the gateway delivers `notification.created` only to this member's connections. */
  member_id: Id,
  kind: NotificationKind,
  actor_member_id: Id.nullable().optional(),
  actor: ActorRef.optional(),
  subject_type: NotificationSubjectType,
  subject_id: Id,
  board_id: Id.nullable().optional(),
  payload: JsonObject,
  read_at: Timestamp.nullable(),
  emailed_at: Timestamp.nullable().optional(),
  created_at: Timestamp,
});
export type Notification = z.infer<typeof Notification>;

export const NOTIFICATIONS_PAGE_MAX = 100;
export const NOTIFICATIONS_READ_IDS_MAX = 500;

export const NotificationPage = cursorPage(Notification).extend({
  /** The caller's unread total (not just this page). */
  unread_count: z.int().min(0),
});
export type NotificationPage = z.infer<typeof NotificationPage>;

export const NotificationsReadRequest = z
  .object({
    ids: z.array(Id).min(1).max(NOTIFICATIONS_READ_IDS_MAX).optional(),
    all: z.boolean().optional(),
  })
  .refine((o) => (o.ids !== undefined) !== (o.all === true), {
    message: 'exactly one of ids or all: true',
  });
export type NotificationsReadRequest = z.infer<typeof NotificationsReadRequest>;

export const NotificationsReadResult = z.object({ updated: z.int().min(0) });
export type NotificationsReadResult = z.infer<typeof NotificationsReadResult>;

export const NotificationPreferences = z.object({
  /** Channel per kind; kinds not listed keep their default. */
  kinds: z.partialRecord(NotificationKind, NotificationChannel),
  digest: NotificationDigest,
  digest_hour_utc: z.int().min(0).max(23),
});
export type NotificationPreferences = z.infer<typeof NotificationPreferences>;

/** Defaults when a member never saved preferences (api and web agree). */
export const NOTIFICATION_PREFERENCE_DEFAULTS: Readonly<NotificationPreferences> = {
  kinds: {
    mention: 'email',
    reply: 'email',
    comment: 'in_app',
    approval: 'in_app',
    upload: 'in_app',
    share_opened: 'in_app',
    share_downloaded: 'in_app',
    guest_upload: 'in_app',
    board_invite: 'email',
    rights_expired: 'digest',
    retention_applied: 'digest',
  },
  digest: 'daily',
  digest_hour_utc: 8,
};

export const BoardActivityScope = z.enum(['comments', 'activity', 'all']);
export type BoardActivityScope = z.infer<typeof BoardActivityScope>;

export const BoardActivityItemKind = z.enum(['comment', 'activity']);
export type BoardActivityItemKind = z.infer<typeof BoardActivityItemKind>;

export const BoardActivityItem = z.object({
  /** Comment id or audit event id. */
  id: Id,
  kind: BoardActivityItemKind,
  created_at: Timestamp,
  actor: ActorRef,
  board_id: Id,
  asset_id: Id.nullable().optional(),
  asset_title: z.string().nullable().optional(),
  /** `comment.created` for comments; the audit `action` otherwise. */
  action: z.string(),
  comment: Comment.optional(),
  metadata: JsonObject.optional(),
});
export type BoardActivityItem = z.infer<typeof BoardActivityItem>;

export const BOARD_ACTIVITY_PAGE_MAX = 100;

export const BoardActivityPage = cursorPage(BoardActivityItem);
export type BoardActivityPage = z.infer<typeof BoardActivityPage>;

/** `notification.created` carries the new row itself (member-scoped delivery, like `pin.changed`). */
export const WsNotificationPayload = Notification;
export type WsNotificationPayload = z.infer<typeof WsNotificationPayload>;

export const WsNotificationReadPayload = z.object({
  workspace_id: Id,
  member_id: Id,
  ids: z.array(Id).max(NOTIFICATIONS_READ_IDS_MAX),
  all: z.boolean(),
});
export type WsNotificationReadPayload = z.infer<typeof WsNotificationReadPayload>;

/**
 * Mention tokens in `Comment.body` (release3-contract.md "Mentions"). Canonical: `@[Display Name](member:<uuid>)`,
 * emitted by the composer when a member is picked and resolved by the server into `Comment.mentions`. Legacy bare
 * `@Handle` (display name without whitespace) is resolved only when exactly one active member has that handle.
 */
export const MENTION_TOKEN_RE = /@\[([^\]\n]{1,120})\]\(member:([0-9a-fA-F-]{36})\)/g;
export const LEGACY_MENTION_RE = /(^|[^\w@\]])@([\p{L}\p{N}_.-]{1,80})/gu;

export const mentionToken = (displayName: string, memberId: string): string =>
  `@[${displayName.replace(/[[\]\n]/g, ' ').trim()}](member:${memberId})`;

/** Canonical tokens of a body, in order, de-duplicated by member id. */
export function parseMentionTokens(body: string): { member_id: string; display_name: string }[] {
  const seen = new Set<string>();
  const out: { member_id: string; display_name: string }[] = [];
  for (const m of body.matchAll(MENTION_TOKEN_RE)) {
    const id = m[2]!.toLowerCase();
    if (seen.has(id)) continue;
    seen.add(id);
    out.push({ member_id: id, display_name: m[1]! });
  }
  return out;
}

/** Legacy `@Handle` candidates (not canonical tokens), lower-cased, de-duplicated. */
export function parseLegacyMentionHandles(body: string): string[] {
  const stripped = body.replace(MENTION_TOKEN_RE, ' ');
  return [...new Set([...stripped.matchAll(LEGACY_MENTION_RE)].map((m) => m[2]!.toLowerCase()))];
}

// --- release3/B --- share-link policy (DEL-1), find similar (SRCH-1), board defaults (META-2)

export const ShareIdentifyRequest = z.object({
  name: z.string().trim().min(1).max(120),
  email: Email,
});
export type ShareIdentifyRequest = z.infer<typeof ShareIdentifyRequest>;

export const SIMILAR_ASSETS_LIMIT_DEFAULT = 24;
export const SIMILAR_ASSETS_LIMIT_MAX = 48;
/** `similar_assets()` over-fetches this many times the limit before the visibility filter. */
export const SIMILAR_ASSETS_OVERFETCH = 4;

export const SimilarAsset = AssetCard.extend({
  /** Cosine similarity (1 = identical embedding). */
  score: UnitInterval,
});
export type SimilarAsset = z.infer<typeof SimilarAsset>;

export const SimilarAssetsPage = z.object({
  items: z.array(SimilarAsset),
  source_version_id: Id,
});
export type SimilarAssetsPage = z.infer<typeof SimilarAssetsPage>;

export const BOARD_DEFAULT_TAGS_MAX = 20;

export const BoardDefaults = z.object({
  tag_ids: z
    .array(Id)
    .max(BOARD_DEFAULT_TAGS_MAX)
    .refine((ids) => new Set(ids).size === ids.length, { message: 'tag_ids must be unique' }),
  /** null = no default (inherit). */
  approval_status: ApprovalStatus.nullable(),
});
export type BoardDefaults = z.infer<typeof BoardDefaults>;

export const BoardDefaultsView = z.object({
  board_id: Id,
  own: BoardDefaults.nullable(),
  effective: BoardDefaults,
  /** Board whose `approval_status` default applies (nearest ancestor-or-self); null when none. */
  source_board_id: Id.nullable(),
});
export type BoardDefaultsView = z.infer<typeof BoardDefaultsView>;

// --- release3/C --- export presets and on-demand export (EXP-1)

export const ExportFit = z.enum(['scale', 'long_edge', 'box']);
export type ExportFit = z.infer<typeof ExportFit>;

export const EXPORT_MAX_EDGE = 8192;
export const EXPORT_MIN_EDGE = 16;
export const EXPORT_SCALES = [1, 0.5, 0.25] as const;
/** Bump when the renderer changes output for the same parameters (part of `transform_key`). */
export const EXPORT_ENGINE_REV = 2; // release4/G (review #14): 2 = cache key without `suffix`, with `quality` for `format: original`
/** `export` renditions unread for this long are removed by `delete-temp-renditions`. */
export const EXPORT_RENDITION_TTL_DAYS = 30;

export const ImageTransform = z
  .object({
    format: ExportFormat,
    fit: ExportFit.optional(),
    scale: z.union([z.literal(1), z.literal(0.5), z.literal(0.25)]).optional(),
    long_edge: z.int().min(EXPORT_MIN_EDGE).max(EXPORT_MAX_EDGE).optional(),
    width: z.int().min(EXPORT_MIN_EDGE).max(EXPORT_MAX_EDGE).optional(),
    height: z.int().min(EXPORT_MIN_EDGE).max(EXPORT_MAX_EDGE).optional(),
    quality: z.int().min(1).max(100).optional(),
    strip_metadata: z.boolean().default(true),
    suffix: z
      .string()
      .max(24)
      .regex(/^[A-Za-z0-9._-]*$/)
      .optional(),
  })
  .refine(
    (t) =>
      t.fit === undefined ||
      (t.fit === 'scale' && t.scale !== undefined) ||
      (t.fit === 'long_edge' && t.long_edge !== undefined) ||
      (t.fit === 'box' && (t.width !== undefined || t.height !== undefined)),
    { message: 'fit needs its size key (scale / long_edge / width or height)', path: ['fit'] },
  );
export type ImageTransform = z.infer<typeof ImageTransform>;

/**
 * Canonical JSON of a transform: sorted keys, defaults filled, size keys of other fits dropped. The worker hashes
 * `sha256(version sha256 + canonical + EXPORT_ENGINE_REV)` into `asset_renditions.transform_key`.
 *
 * release4/G (review #14): `{ forKey: true }` is the cache-key form — it drops `suffix` (a file-name concern: the same
 * pixels are rendered once whatever the suffix) and keeps `quality` for `format: original` too (a jpeg / webp / avif
 * source is re-encoded with it, so two qualities must not share one rendition). `quality` never affects png output.
 * Without the option (job params) the suffix is kept so the worker can name the file.
 */
export function canonicalTransform(t: ImageTransform, opts: { forKey?: boolean } = {}): string {
  const out: Record<string, unknown> = { format: t.format, strip_metadata: t.strip_metadata ?? true };
  if (t.fit) out.fit = t.fit;
  if (t.fit === 'scale') out.scale = t.scale;
  if (t.fit === 'long_edge') out.long_edge = t.long_edge;
  if (t.fit === 'box') {
    if (t.width !== undefined) out.width = t.width;
    if (t.height !== undefined) out.height = t.height;
  }
  if (t.quality !== undefined && t.format !== 'png' && (opts.forKey || t.format !== 'original')) out.quality = t.quality;
  if (t.suffix && !opts.forKey) out.suffix = t.suffix;
  return JSON.stringify(Object.fromEntries(Object.entries(out).sort(([a], [b]) => (a < b ? -1 : 1))));
}

export const DownloadPreset = z.object({
  id: Id,
  workspace_id: Id,
  name: z.string().max(60),
  transform: ImageTransform,
  sort_order: z.int(),
  created_by: Id.nullable().optional(),
  created_at: Timestamp,
  updated_at: Timestamp,
});
export type DownloadPreset = z.infer<typeof DownloadPreset>;

export const DownloadPresetCreate = z.object({
  name: z.string().trim().min(1).max(60),
  transform: ImageTransform,
  sort_order: z.int().optional(),
});
export type DownloadPresetCreate = z.infer<typeof DownloadPresetCreate>;

export const DownloadPresetUpdate = atLeastOneField(
  z.object({
    name: z.string().trim().min(1).max(60).optional(),
    transform: ImageTransform.optional(),
    sort_order: z.int().optional(),
  }),
);
export type DownloadPresetUpdate = z.infer<typeof DownloadPresetUpdate>;

export const WsPresetChangedPayload = z.object({
  workspace_id: Id,
  preset_id: Id.nullable().optional(),
  change: z.enum(['created', 'updated', 'deleted']).optional(),
});
export type WsPresetChangedPayload = z.infer<typeof WsPresetChangedPayload>;

// --- release3/D --- private boards (S2) and board guests (DEL-2)

/** Override mask per guest access level (VIEW=1, COMMENT=4, UPLOAD=8). */
export const GUEST_ACCESS_MASK: Readonly<Record<z.infer<typeof GuestAccess>, number>> = {
  view: 1,
  comment: 1 | 4,
  upload: 1 | 4 | 8,
};

/** Name of the per-workspace guest role seeded by 0007 (`roles.is_guest`): the lower-case `guest` system preset. */
export const GUEST_ROLE_NAME = 'guest';

export const MemberGuestFilter = z.enum(['include', 'exclude', 'only']);
export type MemberGuestFilter = z.infer<typeof MemberGuestFilter>;

export const BoardGuestInvite = z.object({
  email: Email,
  access: GuestAccess,
  message: z.string().max(1000).optional(),
});
export type BoardGuestInvite = z.infer<typeof BoardGuestInvite>;

export const BoardGuest = z.object({
  member: Member,
  access: GuestAccess,
  board_id: Id,
  invited_at: Timestamp,
});
export type BoardGuest = z.infer<typeof BoardGuest>;

export const BoardGuestUpdate = z.object({ access: GuestAccess });
export type BoardGuestUpdate = z.infer<typeof BoardGuestUpdate>;

